import type { Sides } from "@project/emerge-types";
import type { Group } from "three";
import { type Euler, Matrix4, Vector3 } from "three";
import type { ConnectorInfos } from "../interfaces/ConnectorInfos";
import type { CutProperties } from "../interfaces/CutProperties";
import type { GroupAnimationProperties } from "../interfaces/GroupAnimationProperties";
import {
  calculateGroupAnimations,
  updateGroupAnimationRotationPoints,
} from "./GroupAnimationCalculator";

// Vereinfachtes Interface für externe Verwendung
export interface GroupAnimationState {
  isOpen: boolean;
  isAnimating: boolean;
}

interface InternalGroupState {
  isOpen: boolean;
  isAnimating: boolean;
  animationProgress: number;
  groupRef?: Group;
  lastToggleTime?: number;
  animationId?: number;
  initialLocalPosition?: Vector3;
  initialLocalRotation?: Euler;
}

/**
 * Lokale Implementierung der rotateAround Funktion
 * Rotiert ein Objekt um einen gegebenen Pivot-Punkt
 */
function rotateAround(
  object: Group,
  pivot: Vector3,
  rotation: Vector3,
  order = [0, 1, 2],
): void {
  object.matrix.identity();
  object.matrixAutoUpdate = false;
  object.applyMatrix4(
    new Matrix4().makeTranslation(-pivot.x, -pivot.y, -pivot.z),
  );

  for (const axis of order) {
    if (axis === 0) {
      object.applyMatrix4(new Matrix4().makeRotationX(rotation.x));
    }
    if (axis === 1) {
      object.applyMatrix4(new Matrix4().makeRotationY(rotation.y));
    }
    if (axis === 2) {
      object.applyMatrix4(new Matrix4().makeRotationZ(rotation.z));
    }
  }
  object.matrixWorldNeedsUpdate = true;
  object.applyMatrix4(new Matrix4().makeTranslation(pivot.x, pivot.y, pivot.z));
}

export class GroupAnimationManager {
  private groupStates = new Map<string, InternalGroupState>();
  private animationProperties = new Map<string, GroupAnimationProperties>();

  /**
   * Initialisiert den Manager mit Connectors und Cuts
   * Wird automatisch von FlightCaseDefinition aufgerufen
   */
  public initialize(
    connectors: ConnectorInfos[],
    cuts: CutProperties[] = [],
    hingeDefinitions: Array<{
      group?: string;
      position: Vector3;
      partSelection?: { hingeOffset?: number };
      side?: Sides;
    }> = [],
    innerDimensions?: Vector3,
  ): void {
    // Berechne Animationen
    let groupAnimations = calculateGroupAnimations(
      connectors,
      cuts,
      innerDimensions,
    );
    groupAnimations = updateGroupAnimationRotationPoints(
      groupAnimations,
      hingeDefinitions,
    );

    // Initialisiere States
    this.animationProperties.clear();
    this.groupStates.clear();

    groupAnimations.forEach((animation) => {
      this.animationProperties.set(animation.groupName, animation);
      this.groupStates.set(animation.groupName, {
        isOpen: false,
        animationProgress: 0,
        isAnimating: false,
      });
    });
  }

  /**
   * Aktualisiert die Animationseigenschaften basierend auf geänderten Connectors/Cuts
   * Behält bestehende Gruppenstates bei, wenn möglich
   */
  public updateConnectors(
    connectors: ConnectorInfos[],
    cuts: CutProperties[] = [],
    hingeDefinitions: Array<{
      group?: string;
      position: Vector3;
      partSelection?: { hingeOffset?: number };
      side?: Sides;
    }> = [],
    innerDimensions?: Vector3,
  ): void {
    // Berechne neue Animationen
    let newGroupAnimations = calculateGroupAnimations(
      connectors,
      cuts,
      innerDimensions,
    );
    newGroupAnimations = updateGroupAnimationRotationPoints(
      newGroupAnimations,
      hingeDefinitions,
    );

    // Sammle bestehende States
    const existingStates = new Map(this.groupStates);
    const oldAnimationProperties = new Map(this.animationProperties);

    // Aktualisiere Animation Properties
    this.animationProperties.clear();

    newGroupAnimations.forEach((animation) => {
      this.animationProperties.set(animation.groupName, animation);
    });

    // Aktualisiere Group States
    const newGroupStates = new Map<string, InternalGroupState>();

    newGroupAnimations.forEach((animation) => {
      const existingState = existingStates.get(animation.groupName);
      const oldProperties = oldAnimationProperties.get(animation.groupName);

      if (existingState) {
        // Stoppe laufende Animationen
        if (existingState.animationId) {
          cancelAnimationFrame(existingState.animationId);
        }

        // Prüfe ob sich die Connector-Konfiguration geändert hat
        const configurationChanged = this.hasConfigurationChanged(
          oldProperties,
          animation,
        );

        if (
          configurationChanged &&
          existingState.groupRef &&
          existingState.initialLocalPosition &&
          existingState.initialLocalRotation
        ) {
          // Setze Objekt in Ausgangsposition zurück
          existingState.groupRef.position.copy(
            existingState.initialLocalPosition,
          );
          existingState.groupRef.rotation.copy(
            existingState.initialLocalRotation,
          );

          // Setze matrixAutoUpdate zurück auf true für normale Transformationen
          existingState.groupRef.matrixAutoUpdate = true;

          // Setze Animation auf geschlossen zurück
          newGroupStates.set(animation.groupName, {
            ...existingState,
            isOpen: false,
            animationProgress: 0,
            isAnimating: false,
            animationId: undefined,
          });
        } else {
          // Behalte bestehenden State bei, aber aktualisiere die rotationPoint
          // in den Properties, damit die Animation mit den neuen Werten funktioniert
          // (z.B. wenn sich die Tiefe geändert hat)
          newGroupStates.set(animation.groupName, {
            ...existingState,
            isAnimating: false,
            animationId: undefined,
          });
        }
      } else {
        // Erstelle neuen State für neue Gruppe
        newGroupStates.set(animation.groupName, {
          isOpen: false,
          animationProgress: 0,
          isAnimating: false,
        });
      }
    });

    // Entferne States für Gruppen, die keine Connectors mehr haben
    // und setze sie in Ausgangsposition zurück
    for (const [groupName, state] of existingStates) {
      if (!newGroupStates.has(groupName)) {
        if (state.animationId) {
          cancelAnimationFrame(state.animationId);
        }

        // Setze Objekt in Ausgangsposition zurück, wenn es noch existiert
        if (
          state.groupRef &&
          state.initialLocalPosition &&
          state.initialLocalRotation
        ) {
          state.groupRef.position.copy(state.initialLocalPosition);
          state.groupRef.rotation.copy(state.initialLocalRotation);
          state.groupRef.matrixAutoUpdate = true;
        }
      }
    }

    // Setze neue States, damit updateGlobalTransform darauf zugreifen kann
    this.groupStates = newGroupStates;

    // Aktualisiere sofort die Positionen aller Gruppen, die geöffnet sind
    // und deren rotationPoint sich geändert haben könnte
    for (const [groupName, state] of newGroupStates) {
      if (
        state.animationProgress > 0 &&
        state.groupRef &&
        state.initialLocalPosition &&
        state.initialLocalRotation
      ) {
        const props = this.animationProperties.get(groupName);
        if (
          props &&
          props.animationType === "rotation" &&
          props.rotationPoint
        ) {
          // Aktualisiere Position sofort mit neuem rotationPoint
          this.updateGlobalTransform(groupName, state.animationProgress);
        }
      }
    }
  }

  /**
   * Prüft ob sich die Connector-Konfiguration einer Gruppe geändert hat
   * Wichtig: Änderungen der rotationPoint durch Tiefenänderungen werden ignoriert,
   * da diese die grundlegende Konfiguration nicht ändern
   */
  private hasConfigurationChanged(
    oldProperties: GroupAnimationProperties | undefined,
    newProperties: GroupAnimationProperties,
  ): boolean {
    if (!oldProperties) {
      return false; // Neue Gruppe, keine Änderung
    }

    // Prüfe Animationstyp
    if (oldProperties.animationType !== newProperties.animationType) {
      return true;
    }

    // Prüfe Animationsrichtung
    const oldDir = oldProperties.animationDirection;
    const newDir = newProperties.animationDirection;
    if (
      Math.abs(oldDir.x - newDir.x) > 0.001 ||
      Math.abs(oldDir.y - newDir.y) > 0.001 ||
      Math.abs(oldDir.z - newDir.z) > 0.001
    ) {
      return true;
    }

    // Prüfe movementDirection (für Scharniere)
    const oldMovement = oldProperties.movementDirection;
    const newMovement = newProperties.movementDirection;
    if (!oldMovement && newMovement) {
      return true;
    }
    if (oldMovement && !newMovement) {
      return true;
    }
    if (oldMovement && newMovement) {
      if (
        Math.abs(oldMovement.x - newMovement.x) > 0.001 ||
        Math.abs(oldMovement.y - newMovement.y) > 0.001 ||
        Math.abs(oldMovement.z - newMovement.z) > 0.001
      ) {
        return true;
      }
    }

    // Ignoriere Änderungen des rotationPoint, da diese durch Tiefenänderungen
    // verursacht werden können, ohne dass sich die grundlegende Konfiguration ändert
    // Die rotationPoint-Änderungen werden durch die Animation automatisch berücksichtigt

    return false;
  }

  /**
   * Registriert eine Gruppe und speichert die lokalen Koordinaten
   */
  public registerGroup(groupName: string, groupRef: Group): boolean {
    if (!this.animationProperties.has(groupName)) {
      return false; // Keine Animation für diese Gruppe
    }

    const state = this.groupStates.get(groupName);
    if (!state || state.groupRef) {
      return true; // Bereits registriert oder kein State
    }

    // Speichere Referenz und lokale initiale Transformationen
    state.groupRef = groupRef;
    state.initialLocalPosition = groupRef.position.clone(); // Lokale Position
    state.initialLocalRotation = groupRef.rotation.clone(); // Lokale Rotation

    return true;
  }

  /**
   * Schaltet eine Gruppe um - Hauptfunktion für UI
   */
  public toggleGroup(groupName: string): boolean {
    const state = this.groupStates.get(groupName);
    const props = this.animationProperties.get(groupName);

    if (!state || !props || !state.groupRef) {
      return false;
    }

    // Allow toggling while animating so direction can reverse mid-animation.
    this.animateToState(groupName, !state.isOpen);
    return true;
  }

  /**
   * Gibt vereinfachten State zurück
   */
  public getGroupState(groupName: string): GroupAnimationState | undefined {
    const state = this.groupStates.get(groupName);
    return state
      ? {
          isOpen: state.isOpen,
          isAnimating: state.isAnimating,
        }
      : undefined;
  }

  /**
   * Gibt alle States zurück
   */
  public getAllGroupStates(): Map<string, GroupAnimationState> {
    const result = new Map<string, GroupAnimationState>();
    for (const [groupName, state] of this.groupStates) {
      result.set(groupName, {
        isOpen: state.isOpen,
        isAnimating: state.isAnimating,
      });
    }
    return result;
  }

  /**
   * Gibt verfügbare Gruppennamen zurück
   */
  public getAvailableGroupNames(): string[] {
    return Array.from(this.animationProperties.keys());
  }

  /**
   * Prüft ob Gruppe interaktiv ist
   */
  public isGroupInteractive(groupName: string): boolean {
    return this.animationProperties.has(groupName);
  }

  /**
   * Prüft ob Gruppe registriert ist
   */
  public isGroupRegistered(groupName: string): boolean {
    const state = this.groupStates.get(groupName);
    return !!state?.groupRef;
  }

  /**
   * Cleanup
   */
  public dispose(): void {
    // Stoppe alle Animationen
    for (const state of this.groupStates.values()) {
      if (state.animationId) {
        cancelAnimationFrame(state.animationId);
      }
    }
    this.groupStates.clear();
    this.animationProperties.clear();
  }

  // ===== PRIVATE METHODS - Interne Automatisierung =====

  private animateToState(groupName: string, targetOpen: boolean): void {
    const state = this.groupStates.get(groupName);
    const props = this.animationProperties.get(groupName);

    if (
      !state ||
      !props ||
      !state.groupRef ||
      !state.initialLocalPosition ||
      !state.initialLocalRotation
    ) {
      return;
    }

    // Stoppe vorherige Animation
    if (state.animationId) {
      cancelAnimationFrame(state.animationId);
    }

    state.isOpen = targetOpen;
    state.isAnimating = true;
    state.lastToggleTime = Date.now();

    const startProgress = state.animationProgress;
    const targetProgress = targetOpen ? 1 : 0;

    this.startAnimation(groupName, startProgress, targetProgress);
  }

  private startAnimation(
    groupName: string,
    startProgress: number,
    targetProgress: number,
  ): void {
    const state = this.groupStates.get(groupName);
    const props = this.animationProperties.get(groupName);

    if (!state || !props) return;

    const startTime = performance.now();
    const duration = props.duration;
    const easingFunction = props.easing;

    const animate = (currentTime: number) => {
      if (!state.isAnimating) return;

      const elapsed = currentTime - startTime;
      const normalizedTime = Math.min(elapsed / duration, 1);
      const easedTime = easingFunction(normalizedTime);

      const currentProgress =
        startProgress + (targetProgress - startProgress) * easedTime;
      state.animationProgress = currentProgress;

      this.updateGlobalTransform(groupName, currentProgress);

      if (normalizedTime >= 1) {
        state.isAnimating = false;
        state.animationId = undefined;
      } else {
        state.animationId = requestAnimationFrame(animate);
      }
    };

    state.animationId = requestAnimationFrame(animate);
  }

  private updateGlobalTransform(groupName: string, progress: number): void {
    const state = this.groupStates.get(groupName);
    const props = this.animationProperties.get(groupName);

    if (
      !state?.groupRef ||
      !state.initialLocalPosition ||
      !state.initialLocalRotation ||
      !props
    )
      return;

    const direction = new Vector3(
      props.animationDirection.x,
      props.animationDirection.y,
      props.animationDirection.z,
    );

    if (props.animationType === "linear") {
      const maxDistance = props.maxDistance || 200;
      const offset = direction.clone().multiplyScalar(progress * maxDistance);
      const newPosition = state.initialLocalPosition.clone().add(offset);

      state.groupRef.position.copy(newPosition);
    } else if (props.animationType === "rotation") {
      const maxAngle = props.maxAngle || (Math.PI * 2) / 3; // Default: 120 Grad
      const currentAngle = progress * maxAngle;

      // Verwende rotateAround für Rotation um den berechneten Rotationspunkt
      if (props.rotationPoint) {
        // Erstelle Rotationspunkt als Vector3
        const rotationPoint = new Vector3(
          props.rotationPoint.x,
          props.rotationPoint.y,
          props.rotationPoint.z,
        );

        // Berechne die Rotation basierend auf dem aktuellen Fortschritt
        const rotationVector = direction.clone().multiplyScalar(currentAngle);

        // Setze das Objekt zurück auf die initiale Position/Rotation
        state.groupRef.position.copy(state.initialLocalPosition);
        state.groupRef.rotation.copy(state.initialLocalRotation);

        // Wende die Rotation um den Rotationspunkt an
        rotateAround(state.groupRef, rotationPoint, rotationVector);
      }
    }
  }
}
